import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PageHeader } from "components/common";
import type { ImportPartnersResult, ImportedPartner } from "api";

export default function ImportPartnersSummaryPage() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const result    = (location.state as { result: ImportPartnersResult } | null)?.result;

  if (!result) {
    navigate("/tools/import-partners");
    return null;
  }

  const { summary, partners, errors } = result;

  return (
    <div className="px-4 py-6 sm:px-8 sm:py-8">
      <PageHeader title="Import complete" subtitle="Review the results of your bulk partner import." />

      {/* Summary counts */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 mb-6">
        <SummaryCard label="Processed" value={summary.processed} color="gray" />
        <SummaryCard label="Created"   value={summary.created}   color="green" />
        <SummaryCard label="Skipped"   value={summary.skipped}   color="yellow" />
        <SummaryCard label="Errored"   value={summary.errored}   color="red" />
      </div>

      {/* Errors */}
      {errors.length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-semibold text-gray-800 mb-2">Row errors</h2>
          <div className="space-y-1.5">
            {errors.map((e, i) => (
              <div key={i} className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
                <span className="font-medium">Row {e.row}:</span> {e.error}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Partners table */}
      {partners.length > 0 ? (
        <div>
          <h2 className="text-sm font-semibold text-gray-800 mb-2">Partners created</h2>
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium text-gray-600">Name</th>
                  <th className="text-left px-4 py-2.5 font-medium text-gray-600">Type</th>
                  <th className="text-left px-4 py-2.5 font-medium text-gray-600">ID</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {partners.map((p: ImportedPartner) => (
                  <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-2.5 text-gray-800 font-medium">{p.name}</td>
                    <td className="px-4 py-2.5">
                      <TypeBadge type={p.type} />
                    </td>
                    <td className="px-4 py-2.5 text-gray-400">{p.id}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <p className="text-xs text-gray-500">No partners were created.</p>
      )}

      <button
        className="btn-secondary mt-6"
        onClick={() => navigate("/tools/import-partners")}
      >
        Import another file
      </button>
    </div>
  );
}

function SummaryCard({ label, value, color }: { label: string; value: number; color: "gray" | "green" | "yellow" | "red" }) {
  const colors = {
    gray:   "bg-gray-50 text-gray-800",
    green:  "bg-green-50 text-green-800",
    yellow: "bg-yellow-50 text-yellow-800",
    red:    "bg-red-50 text-red-800",
  };
  return (
    <div className={`rounded-lg px-4 py-3 ${colors[color]}`}>
      <p className="text-xl font-bold">{value}</p>
      <p className="text-xs opacity-70 mt-0.5">{label}</p>
    </div>
  );
}

function TypeBadge({ type }: { type: string }) {
  const styles: Record<string, string> = {
    donator:     "bg-blue-100 text-blue-700",
    distributor: "bg-purple-100 text-purple-700",
    ops:         "bg-gray-100 text-gray-700",
  };
  return (
    <span className={`inline-block rounded px-1.5 py-0.5 font-medium capitalize ${styles[type] ?? "bg-gray-100 text-gray-700"}`}>
      {type}
    </span>
  );
}
